import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Button, StatusBadge, Timestamp, useToast } from "@/design-system/components/ui";
import { extractErrorMessage } from "@/api/client";
import { buyersApi } from "@/api/buyers";
import { buyerKeys } from "@/hooks/buyers/buyerKeys";
import { DetailSection } from "../detailBits";
import { SendPreviewModal } from "../sendPreview/SendPreviewModal";
import { useSenderIdentity } from "../sendPreview/useSenderIdentity";

/**
 * "Educate the Buyer" — the Qualification tab's second step.
 *
 * <p>Qualification is two things: knowing the buyer, and having told them how a
 * Phoenix transaction works. A buyer who cannot answer a broker's questions
 * about the financing is a weaker candidate however well they assess, so this
 * card sits at equal weight beside the assessment and the tab reads as two steps
 * outstanding rather than one.</p>
 *
 * <p>🔴 <b>The send history is the Communications tab, not a table here.</b>
 * The send writes a CommunicationLogEntry, which is where a colleague already
 * looks for "has anyone contacted this buyer" — and a card-local table would be
 * a second place the same fact is written. The card therefore states the stamp
 * and LINKS THROUGH; a card saying "sent 12 Aug" with no way to the message is
 * a destination built without a door.</p>
 */
export function BuyerEducationCard({
  buyerLeadId,
  canEdit,
  onOpenCommunications,
}: {
  buyerLeadId: string;
  canEdit: boolean;
  /** Takes the reader to the Communications tab, where the send actually is. */
  onOpenCommunications: () => void;
}) {
  const qc = useQueryClient();
  const toast = useToast();
  const identity = useSenderIdentity();

  const [previewing, setPreviewing] = useState(false);
  // The specialist's own edit, held only while the modal is open. Null means
  // "unchanged" so the server's resolved copy is used verbatim — an empty
  // string is a deliberate clearing and is a different thing.
  const [subject, setSubject] = useState<string | null>(null);
  const [body, setBody] = useState<string | null>(null);
  // The URL to offer as a real anchor when the browser refuses the popup — see
  // openBrochure below. A silently-swallowed window.open is a failure this
  // codebase has shipped before.
  const [blockedBrochureUrl, setBlockedBrochureUrl] = useState<string | null>(null);

  const state = useQuery({
    queryKey: buyerKeys.educationEmail(buyerLeadId),
    queryFn: () => buyersApi.getEducationEmail(buyerLeadId),
    // Only when the modal is open: this resolves the whole template body, and
    // the card itself renders off the buyer detail it already has.
    enabled: previewing,
  });

  const close = () => {
    setPreviewing(false);
    setSubject(null);
    setBody(null);
    setBlockedBrochureUrl(null);
  };

  const send = useMutation({
    mutationFn: () =>
      buyersApi.sendEducationEmail(buyerLeadId, {
        subject: subject ?? undefined,
        body: body ?? undefined,
      }),
    onSuccess: (result) => {
      // The buyer prefix covers the detail — which carries the stamp this card,
      // the Overview milestone and the tab's outstanding-step count all read —
      // as well as the education-email key itself.
      qc.invalidateQueries({ queryKey: buyerKeys.all });
      toast.success(
        result.communicationLogged
          ? "Education email sent."
          : "Education email sent, but it was not written to the communications log.",
      );
      close();
    },
    onError: (e: unknown) => toast.error(extractErrorMessage(e)),
  });

  const markSent = useMutation({
    mutationFn: () =>
      buyersApi.markEducationEmailSent(buyerLeadId, { body: body ?? undefined }),
    onSuccess: () => {
      // Same prefix, same reason: the manual path stamps the same columns, so
      // the same surfaces go stale.
      qc.invalidateQueries({ queryKey: buyerKeys.all });
      toast.success("Recorded as sent.");
      close();
    },
    onError: (e: unknown) => toast.error(extractErrorMessage(e)),
  });

  const s = state.data;
  const pending = send.isPending || markSent.isPending;

  /**
   * Fetch a fresh link to the brochure and hand it over.
   *
   * <p>🔴 The link is minted at CLICK time and never cached, so what the
   * browser is given has just been confirmed to resolve — a SAS expires, and a
   * link fetched when the modal opened is a dead download by the time somebody
   * has read a page of copy. The cost is that `window.open` is a POST-AWAIT
   * call, which popup blockers treat as unrequested, so its null return is
   * checked and the URL offered as a real anchor instead.</p>
   *
   * <p>The affordance itself renders only when the draft says a brochure is
   * configured, so a link is never offered for a file that is not there.</p>
   */
  const openBrochure = async () => {
    setBlockedBrochureUrl(null);
    try {
      const link = await buyersApi.getEducationBrochureLink(buyerLeadId);
      if (!link.available || !link.url) {
        toast.error("The buyer brochure could not be retrieved just now.");
        return;
      }
      const opened = window.open(link.url, "_blank", "noopener");
      if (!opened) setBlockedBrochureUrl(link.url);
    } catch (e) {
      toast.error(extractErrorMessage(e));
    }
  };

  // 🔴 Rendered on EVERY path, not only the send. On "I sent it myself" and
  // "Copy email body" Phoenix attaches nothing — the specialist does — so the
  // one place that can tell them which file to attach is this list. The modal
  // shows it above the footer, so it is visible whichever button they reach for.
  const attachments = s?.brochureFileName
    ? [{
        name: s.brochureFileName,
        sizeBytes: s.brochureSizeBytes ?? null,
        onDownload: () => void openBrochure(),
      }]
    : undefined;

  return (
    <>
      <DetailSection
        title="Buyer education"
        action={
          canEdit ? (
            <Button
              variant="text"
              size="sm"
              onClick={() => setPreviewing(true)}
              data-testid="education-send"
            >
              {/* The label states which of the two this is — a "Send" that
                  silently re-sends is how a buyer receives it twice. */}
              Send education email
            </Button>
          ) : null
        }
      >
        <EducationState
          onOpenCommunications={onOpenCommunications}
          buyerLeadId={buyerLeadId}
        />
      </DetailSection>

      {previewing && (
        <SendPreviewModal
          title="Send the education email"
          onClose={close}
          toName={s?.buyerName}
          toEmail={s?.toEmail}
          audienceNote="It goes to the buyer. Nothing is sent to a listing broker or a seller."
          identity={identity}
          subject={subject ?? s?.subject}
          onSubjectChange={setSubject}
          body={body ?? s?.body}
          onBodyChange={setBody}
          bodyRows={18}
          attachments={attachments}
          // 🔴 The reason was FETCHED and never rendered: the card disabled the
          // button on `canSend` and left the specialist to guess. It renders
          // beside the disabled confirm now, which is what the prop is for.
          //
          // It sits OUTSIDE the message block, so it is still shown while "I
          // sent it myself" stays live — a specialist about to assert they sent
          // this should see what is missing before they do.
          confirmBlockedReason={s && !s.canSend ? s.blockedReason : undefined}
          notices={
            blockedBrochureUrl ? (
              <div className="text-caption text-onyx" role="alert">
                Your browser blocked the download window.{" "}
                <a
                  href={blockedBrochureUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-gold-shadow hover:underline"
                >
                  Open the buyer brochure
                </a>
                .
              </div>
            ) : undefined
          }
          // The editable textarea above is untouched — this ADDS a faithful
          // rendering beneath it. The specialist keeps the per-send edit, and now
          // also sees what the edit will actually look like in an inbox.
          renderPreview={({ subject: subj, body: bod }) =>
            buyersApi
              .previewOutreach(buyerLeadId, { subject: subj, body: bod })
              .then((r) => r.html)
          }
          // While the read is in flight there is nothing honest to show, and a
          // blank editable box would look like copy someone deleted.
          replacesPreviewBody={
            state.isLoading ? (
              <p className="text-small text-gray-mid">Loading the draft…</p>
            ) : undefined
          }
          footnote={
            <>
              Phoenix's role statement is added to every email we send and is not part of
              the text above. Sending records this on the buyer and on their
              Communications tab; it does not change their status or their pipeline
              position.
            </>
          }
          alternateAction={
            <Button
              variant="text"
              size="sm"
              onClick={() => markSent.mutate()}
              disabled={pending}
              data-testid="education-mark-sent"
            >
              I sent it myself
            </Button>
          }
          secondaryAction={
            <Button
              variant="text"
              size="sm"
              disabled={!s?.body}
              onClick={() => {
                void navigator.clipboard?.writeText(
                  `${subject ?? s?.subject ?? ""}\n\n${body ?? s?.body ?? ""}`,
                );
                toast.success("Copied.");
              }}
            >
              Copy email body
            </Button>
          }
          confirmLabel="Send email"
          confirmTestId="education-confirm"
          onConfirm={() => send.mutate()}
          confirmDisabled={!s?.canSend || pending || state.isLoading}
          pending={pending}
        />
      )}
    </>
  );
}

/**
 * The card body: not sent · sent by Phoenix · sent manually.
 *
 * <p>Reads the stamp off the buyer detail already in cache rather than issuing
 * its own request — the card is on screen whether or not anyone opens the
 * preview, and a second query for three fields the page has already loaded is
 * cost with no answer attached.</p>
 */
function EducationState({
  buyerLeadId,
  onOpenCommunications,
}: {
  buyerLeadId: string;
  onOpenCommunications: () => void;
}) {
  const detail = useQuery({
    queryKey: buyerKeys.detail(buyerLeadId),
    queryFn: () => buyersApi.get(buyerLeadId),
  });

  const b = detail.data;
  if (!b) return null;

  if (!b.educationSentAtUtc) {
    return (
      <div className="text-small text-gray-mid">
        Not sent yet. Sending it before we present this buyer is what lets them answer a
        broker's questions about the financing in their own words.
      </div>
    );
  }

  return (
    <div className="grid gap-space-2">
      <div className="flex flex-wrap items-center gap-space-2">
        <StatusBadge tone="ok">Sent</StatusBadge>
        <span className="text-small text-onyx">
          <Timestamp value={b.educationSentAtUtc} />
          {b.educationSentByName ? ` by ${b.educationSentByName}` : ""}
        </span>
        {/* 🔴 Marked, always. A self-attested send is the same work and nothing
            scores differently on it, but the record must not imply Phoenix
            delivered a message it never handled. */}
        {b.educationSentSelfAttested && (
          <span className="text-caption text-gray-mid">
            sent from the consultant's own email
          </span>
        )}
      </div>
      <button
        type="button"
        className="justify-self-start text-small text-gold-shadow hover:underline"
        onClick={onOpenCommunications}
        data-testid="education-view-communications"
      >
        View it on the Communications tab
      </button>
    </div>
  );
}
